import React from 'react'
import { ImageBackground, TouchableWithoutFeedback, View } from 'react-native'
import { useRoute } from '@react-navigation/core'
import { RouteProp, useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { Text } from '@components/base'
import { BenefitCard } from '@screens/Marketplace/components/NewMarketplace/ProductExplanation/BenefitCard.tsx'
import { AppStackParamList } from '@navigation/types'
import { uhcDeepLinkSelector, userSelector, videoCallProductSelector } from '@selectors/app.ts'
import { uiFlagsSelector } from '@selectors/uiFlags'
import { useNavigateToByosUpgrade, useProducts } from '@screens/Marketplace/utils/hooks.ts'
import { CheckoutKind } from '@screens/Marketplace/types/types.ts'
import {
  getProductExplanationScreenDefinition,
  ProductExplanationScreenTypes,
  VideoCallProductTypes,
} from '@screens/Marketplace/components/NewMarketplace/definitions.tsx'
import { getVideoCallProductInfo } from '@src/hooks/useVideoCallProductType.ts'
import { marketplaceNutritionistSupportImage } from '@src/assets/images'
import { openUrl } from '@utils'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { WhiteText } from './WhiteText'

const TAP_ANYWHERE_LABEL = 'Tap anywhere to continue'

export const ProductExplanation = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const { navigateToByosUpgrade } = useNavigateToByosUpgrade()
  const uhcDeepLinks = useSelector(uhcDeepLinkSelector)

  const insets = useSafeAreaInsets()

  const user = useSelector(userSelector)
  const { insuranceInformationSubmitted } = useSelector(uiFlagsSelector)
  const paidVideoCallProduct = useSelector(videoCallProductSelector)
  const { activeSubscriptions } = useProducts()
  const coreSubscription = activeSubscriptions.find(
    (sub) => sub.id === user?.lastCoreSubscription?.id,
  )
  const route = useRoute<RouteProp<AppStackParamList, 'ProductExplanation'>>()
  const params = route.params

  const getDefinition = () => {
    if ('upsellOffer' in params) {
      const { upsellOffer } = params
      return {
        title: (
          <WhiteText type="title-1" style={styles.title}>
            {upsellOffer.benefitsTitle || ''}
          </WhiteText>
        ),
        description: upsellOffer.benefitsDescription,
        backgroundImage: marketplaceNutritionistSupportImage,
        backgroundImageColor: '#122D36',
        backgroundImageOpacity: 0.5,
        benefits: upsellOffer.benefits.map((benefit) => ({
          title: benefit.title,
          description: benefit.description,
          icon: benefit.icon,
        })),
      }
    }

    const { screen } = params
    const { videoCallProductType } = getVideoCallProductInfo(user?.features || [], screen)

    return getProductExplanationScreenDefinition(videoCallProductType, paidVideoCallProduct)[screen]
  }

  const definition = getDefinition()

  useLazyScreenPreload('ChurnRecoveryUpsellOfferConfirmation')

  const onPress = () => {
    if ('upsellOffer' in params) {
      const { upsellOffer } = params
      navigation.replace('ChurnRecoveryUpsellOfferConfirmation', {
        subscriptionId: upsellOffer.subscriptionId,
        billingProduct: upsellOffer.billingProduct,
        billingProductPrice: upsellOffer.billingProductPrice,
        billingProductIntervalDescription: upsellOffer.billingProductName,
      })
      return
    }

    const { screen } = params
    const { videoCallProductType } = getVideoCallProductInfo(user?.features || [], screen)

    switch (screen) {
      case ProductExplanationScreenTypes.CgmProducts: {
        navigation.navigate('SteloSelectSubscription', {
          checkoutKind: CheckoutKind.UpgradeProduct,
          subscriptionId: coreSubscription?.id,
        })
        break
      }
      case ProductExplanationScreenTypes.OwnSensor: {
        navigateToByosUpgrade(true)
        break
      }
      case ProductExplanationScreenTypes.PaidVideoCalls: {
        const { appointmentKind } = paidVideoCallProduct?.ehrSchedulingRule ?? {}
        if (!paidVideoCallProduct || !appointmentKind) {
          break
        }
        // TODO: use scheduling info to get the pay-as-you-product or hide path once unified booking flows are enabled
        //
        navigation.replace('VideoCallCheckout')
        break
      }
      case ProductExplanationScreenTypes.InsuranceVideoCalls: {
        if (videoCallProductType === VideoCallProductTypes.HealthCoaching) {
          openUrl(uhcDeepLinks.storeUrl)
          break
        }

        if (insuranceInformationSubmitted) {
          navigation.replace('Drawer', {
            screen: 'Dashboard',
            params: { screen: 'NutritionistHub' },
          })
        } else {
          navigation.replace('InsuranceQuestionnaire', {
            skipIntroScreen: true,
          })
        }
        break
      }
    }
  }

  const {
    title,
    description,
    backgroundImage,
    backgroundImageColor,
    backgroundImageOpacity,
    benefits,
  } = definition

  return (
    <TouchableWithoutFeedback
      style={styles.container}
      accessibilityLabel={TAP_ANYWHERE_LABEL}
      onPress={onPress}
    >
      <View style={styles.imageContainer}>
        <ImageBackground source={backgroundImage.imageSource} style={styles.imageBackground}>
          <View
            style={[
              styles.layer,
              { backgroundColor: backgroundImageColor, opacity: backgroundImageOpacity },
            ]}
          />

          <View style={styles.content}>
            <View style={{ marginTop: 64 + insets.top }}>
              {title}
              <Text type="large" style={[styles.whiteText, { marginTop: 12 }]}>
                {description}
              </Text>
            </View>

            <View>
              {benefits.map((benefit) => {
                return <BenefitCard key={benefit.title} benefits={benefits} benefit={benefit} />
              })}

              <View
                style={[
                  styles.bottomLabelContainer,
                  { paddingBottom: Math.max(insets.bottom, 32) },
                ]}
              >
                <Text type="large" bold style={styles.whiteText}>
                  {TAP_ANYWHERE_LABEL}
                </Text>
              </View>
            </View>
          </View>
        </ImageBackground>
      </View>
    </TouchableWithoutFeedback>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
  },
  content: {
    justifyContent: 'space-between',
    marginHorizontal: 16,
    flex: 1,
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageBackground: {
    flex: 1,
    width: '100%',
  },
  whiteText: {
    color: 'theme.solid.white',
  },
  title: {
    fontSize: 29,
    lineHeight: 31,
    fontWeight: 400,
  },
  bottomLabelContainer: {
    alignItems: 'center',
    marginTop: 24,
  },
  layer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
})
